/**
 * PATH: packages/ui/src/kanban/components/label-picker/parts.tsx
 * FILE_ID: UI-KANBAN-LABEL-PICKER-PARTS-001
 * PURPOSE: Small I→O pieces for the card hover label picker's INLINE editing —
 *   a mini colour palette, a per-row expandable editor (rename + recolour +
 *   archive), and a "New label" composer. No mode toggle: every affordance
 *   lives on the row it affects.
 * WHY: FEAT-2026-07-04-KANBAN-LABEL-PICKER-V2 — operator: "The pencil should
 *   have been in EACH label … in the list I should be able to add a new label
 *   and select the color … and change the color by each label box".
 * FLOW:
 *   ┌────────────┐ pick  ┌──────────────────────┐
 *   │ MiniPalette │ ────▶ │ onPick(color)         │ (parent persists via api)
 *   └────────────┘       └──────────────────────┘
 * DEPENDENCIES:
 *   - ../card-detail/LabelManagerPanel: LABEL_FILL_PALETTE (single palette
 *     source — picker and drawer/settings editors stay in sync).
 * RELATED:
 *   - ../KanbanCardLabelPicker.tsx: hosts these per label row.
 */
import { useState } from 'react'
import { Check, Loader2 } from 'lucide-react'

import type { Tag } from '../../types'
import { LABEL_FILL_PALETTE } from '../card-detail/LabelManagerPanel'

/** Compact swatch strip. Pure: paints `value` selected, reports picks up. */
export function MiniPalette({
  value,
  onPick,
  busy,
  testIdPrefix,
}: {
  value: string
  onPick: (color: string) => void
  busy?: boolean
  testIdPrefix: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Label colour">
      {LABEL_FILL_PALETTE.map((c) => {
        const isSel = value.toLowerCase() === c.toLowerCase()
        return (
          <button
            key={c}
            type="button"
            disabled={busy}
            data-testid={`${testIdPrefix}-${c.replace('#', '')}`}
            aria-label={`Colour ${c}`}
            title={c}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onPick(c)
            }}
            style={{ background: c }}
            className={[
              'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
              'shadow-sm ring-1 ring-black/10 transition-transform hover:scale-110 disabled:opacity-50',
              'dark:ring-white/15',
              isSel ? 'ring-2 ring-offset-1 ring-neutral-700 dark:ring-white dark:ring-offset-zinc-900' : '',
            ].join(' ')}
          >
            {isSel && <Check className="h-3 w-3 text-white drop-shadow" aria-hidden="true" />}
          </button>
        )
      })}
    </div>
  )
}

/** Shared editor body: name input + palette + action row. Used for BOTH the
 *  per-row pencil editor and the "New label" composer (one editor, no fork). */
export function InlineLabelForm({
  initial,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
  onArchive,
  testIdPrefix,
}: {
  initial: Pick<Tag, 'name' | 'color'>
  submitLabel: string
  busy?: boolean
  onSubmit: (draft: { name: string; color: string }) => void
  onCancel: () => void
  /** Present only for existing labels (the pencil editor). */
  onArchive?: () => void
  testIdPrefix: string
}) {
  const [name, setName] = useState(initial.name)
  const [color, setColor] = useState(initial.color || LABEL_FILL_PALETTE[7])
  const canSubmit = !!name.trim() && !busy

  return (
    <div
      className="space-y-1.5 rounded-md bg-neutral-50 p-1.5 dark:bg-zinc-800/60"
      data-testid={`${testIdPrefix}-form`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        value={name}
        autoFocus
        placeholder="Label name…"
        data-testid={`${testIdPrefix}-name`}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter' && canSubmit) onSubmit({ name: name.trim(), color })
          if (e.key === 'Escape') onCancel()
        }}
        className="w-full rounded border border-neutral-200 bg-white px-1.5 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
      />
      <MiniPalette value={color} onPick={setColor} busy={busy} testIdPrefix={`${testIdPrefix}-color`} />
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={!canSubmit}
          data-testid={`${testIdPrefix}-submit`}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onSubmit({ name: name.trim(), color })
          }}
          className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy && <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden="true" />}
          {submitLabel}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onCancel()
          }}
          className="rounded px-2 py-0.5 text-[11px] text-neutral-500 hover:bg-neutral-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
        >
          Cancel
        </button>
        {onArchive && (
          <button
            type="button"
            disabled={busy}
            data-testid={`${testIdPrefix}-archive`}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onArchive()
            }}
            className="ml-auto rounded px-2 py-0.5 text-[11px] text-red-500 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-950/40"
          >
            Archive
          </button>
        )}
      </div>
    </div>
  )
}
